import { Injectable, OnDestroy } from '@angular/core';
import { AppService, PlatformType } from './app.service';
import { AppUpdate, AppUpdateProvider } from '../providers/app-update.provider';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { version } from '../../../package.json';
import { SplashScreenService } from './splash-screen.service';
import { AppLauncher } from '@capacitor/app-launcher';
import { BrowserService } from './browser.service';
import { runtimeEnvironment } from '../../environments/environment.r';
import { Feature, LoggerService } from './logger.service';
import { WebAppUpdateAvailableModalPage } from '../modals/web-app-update-available/web-app-update-available-modal.page';
import { ComponentRef } from '@ionic/core';
import { NavigationError, Router } from '@angular/router';
import { ModalService } from './modal.service';
import { AppState } from '../store/app.state';
import { NativeAppUpdateAvailableModalPage } from '../modals/native-app-update-available/native-app-update-available-modal.page';
import { Platform } from '@ionic/angular/standalone';
import { differenceInDays } from 'date-fns';

@Injectable({ providedIn: 'root' })
export class AppUpdateService implements OnDestroy {
  private appUpdates: Array<AppUpdate> = [];
  private readonly currentVersion = version;
  private destroyed$ = new Subject<void>();

  constructor(
    private appService: AppService,
    private appUpdateProvider: AppUpdateProvider,
    private splashScreenService: SplashScreenService,
    private browserService: BrowserService,
    private router: Router,
    private modalService: ModalService,
    private loggerService: LoggerService,
    private appState: AppState,
    private platform: Platform,
  ) {}

  async initialize(): Promise<void> {
    if (this.appService.platformType === PlatformType.IOS || this.appService.platformType === PlatformType.Android) {
      return this.initializeNative();
    } else {
      return this.initializeWeb();
    }
  }

  private initializeWeb(): void {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationError) {
        const error = event.error;
        if (error.message.includes('Failed to fetch dynamically imported module') || error.message.includes('Importing a module script failed')) {
          void this.updateWebApp(event.url ?? '/home');
        }
      }
    });
  }

  async initializeNative(): Promise<void> {
    this.platform.resume.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      if (this.appState.app$.value.lastForceUpdateCheck && differenceInDays(new Date(), this.appState.app$.value.lastForceUpdateCheck) > 1) {
        void this.appState.set({ lastForceUpdateCheck: new Date() });
        void this.checkForcedNativeUpdate();
      }
    });

    await this.checkForcedNativeUpdate();
    await this.checkOptionalNativeUpdate();
  }

  async goToStore(): Promise<void> {
    const packageName = this.appService.platformType === PlatformType.Android ? 'org.worldreader.readtokids' : 'org.worldreader.booksmart';
    const url = `market://details?id=${packageName}`;

    const canOpen = await AppLauncher.canOpenUrl({ url });

    if (canOpen.value) {
      await AppLauncher.openUrl({ url });
    } else
      this.browserService.goTo(
        this.appService.platformType === PlatformType.Android
          ? 'https://play.google.com/store/apps/details?id=org.worldreader.readtokids'
          : 'https://apps.apple.com/us/app/booksmart-by-worldreader/id1629873557',
      );
  }

  async updateWebApp(url: string): Promise<void> {
    // set build number in localstorage
    if (this.appState.app$.value.lastAppUpdateBuildNumber === runtimeEnvironment.BUILD_NUMBER) {
      this.loggerService.error('AppService::updateApp', {
        feature: Feature.APP_INIT,
        context: 'Could not persistent fetch dynamically imported module after app reload',
      });
      return;
    }

    try {
      const categoriesModal = await this.modalService.create({
        componentProps: { url },
        component: WebAppUpdateAvailableModalPage as ComponentRef,
        cssClass: 'wr-modal web-app-update',
        canDismiss: false,
        backdropDismiss: false,
      });

      categoriesModal && (await categoriesModal.present());
    } catch {
      await this.appState.set({ lastAppUpdateBuildNumber: runtimeEnvironment.BUILD_NUMBER });
      window.location.href = url;
      this.loggerService.error('AppUpdateService::updateApp', {
        feature: Feature.APP_INIT,
        context: 'Could not display WebAppUpdateAvailableModalComponent modal',
      });
    }
  }

  private async checkOptionalNativeUpdate(): Promise<void> {
    const optionalUpdate = this.appUpdates.find(appUpdate => !appUpdate.isBlocker);
    // If there's a new non blocking version, check if dismissed and show the modal (or not)
    if (optionalUpdate && !(await firstValueFrom(this.appUpdateProvider.getUserUpdateDismissed(optionalUpdate.uuid)))) {
      const updateNativeModal = await this.modalService.create({
        component: NativeAppUpdateAvailableModalPage as ComponentRef,
        cssClass: 'wr-modal not-fullscreen',
      });

      await updateNativeModal.present();
      await updateNativeModal.onDidDismiss();

      void firstValueFrom(this.appUpdateProvider.setUserUpdateDismissed(optionalUpdate.uuid));
    }

    return Promise.resolve();
  }

  private async checkForcedNativeUpdate(): Promise<void> {
    const appUpdates = await firstValueFrom(this.appUpdateProvider.getAppUpdates());
    this.appUpdates = appUpdates.filter(appUpdate => Number(appUpdate.targetVersion.replace(/\./g, '')) > Number(this.currentVersion.replace(/\./g, '')));

    // If there's a new blocking version, show the blocking modal directly
    if (this.appUpdates.find(appUpdate => appUpdate.isBlocker)) {
      this.splashScreenService.showSplashUpdateApp();
      return Promise.resolve();
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
